import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAgreementApi } from '@hero/api-hooks';
import type { Client } from '@hero/schema';
import { getObjectId } from '@hero/schema';
import { useCurrentUser } from '@hero/hooks-shared';
import { PricingSheetPage } from '../../../Pricing/PricingSheet/PricingSheetPage';
import type { ScenarioLabels } from '../../../Pricing/PricingSheet/PricingSheetHeader';
import { getAgreementCounterparty } from '../../../Agreement/utils';
import type { SheetScope } from '../../../Pricing/PricingSheet/types';

export interface ClientPricingTabProps {
  client: Client;
  displayForUser: 'customer' | 'carrier';
}

/**
 * Per-client Pricing tab — reuses the standalone `PricingSheetPage` and
 * locks its scope to this Client. The pricing sheet itself owns the
 * table / filters / save flow; we only provide the pre-seeded scope and
 * hide its scope picker so the user can't accidentally re-aim the sheet
 * at someone else from inside the client workspace.
 *
 * Contract envelope (agreement summary + create CTA) intentionally
 * lives on the Info tab now (`AgreementInfoSection`) — keeping it
 * here on top of a dense pricing sheet made the surface feel
 * crowded, and contract metadata (dates, payment terms, monthly
 * volume) reads more naturally next to legal / order defaults than
 * above the rate table.
 *
 * Counterparty resolution: the direct-scope counterparty is the Client's
 * own `_id`. The backend's `RouteDTOService.getPartyRelation` probes
 * `Org` (Hero account) first, then `Client` (CRM-only) — so passing
 * `client._id` resolves uniformly whether the client is a pure-CRM
 * record OR a linked Hero Org. The bridged `profile_id` / `org_id`
 * identifiers (when `client.linkedParty` is set) are stamped into
 * `Party.ids[]` server-side via `PartyResolverService.makeParty`.
 *
 * Agreement deep-link: when the URL carries `?agreementId=…`, the tab
 * fetches the agreement, derives its counterparty Hero org from
 * `parties[]`, and seeds the sheet with `{ kind: 'agreement' }` instead
 * of `{ kind: 'direct' }`. This is how the AgreementDrawer's "Add route"
 * / "Edit route" flow lands the user on the right scoped price list —
 * the legacy `RouteEditModal` is no longer mounted from that drawer.
 *
 * Side default (sell / buy): we don't override — the sheet store's
 * baseline (`side: 'sell'`) wins. Role-based defaulting was tried
 * (`defaultPricingSide(orgRoles, clientRoles)`) but pulled in too
 * much implicit state for what's effectively a one-shot initial
 * state. The sell/buy toggle stays user-driven; "selling price" is
 * the universal landing because most workspace flows are seller-side.
 * Counterparty-aware labels still rename the toggle so the meaning is
 * unambiguous regardless of which side wins.
 */
export function ClientPricingTab({
  client,
  displayForUser: _displayForUser,
}: ClientPricingTabProps) {
  const { t } = useTranslation(['m-pricing']);
  const { orgId } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const agreementIdParam = searchParams.get('agreementId') ?? undefined;

  // Counterparty-aware labels for the sell/buy toggle. Falls back to
  // the generic copy in PricingSheetHeader when the name is missing
  // (rare — `Client.name` is required at create).
  const scenarioLabels = useMemo<ScenarioLabels>(() => {
    const name = client.name ?? '';
    return {
      sell: t('sheet.header.scenario.sell_to', {
        defaultValue: 'Selling price to {{name}}',
        name,
      }),
      buy: t('sheet.header.scenario.buy_from', {
        defaultValue: 'Buying price from {{name}}',
        name,
      }),
    };
  }, [client.name, t]);

  // Pull the agreement when the URL deep-links one — used to label the
  // sheet header. The sheet only needs the id + a display label, so we
  // derive the label from the agreement's title when it loads.
  const agreementQuery = useAgreementApi(agreementIdParam);
  const agreement = agreementQuery.data;

  const initialScope = useMemo<SheetScope | undefined>(() => {
    // Counterparty for the per-client direct scope is the Client itself.
    // `client.org` is the CRM-OWNING org (= the logged-in user), NOT the
    // counterparty — seeding from it would collapse seller and buyer to
    // the same id and trip `route_self_route_not_allowed`.
    const clientScopeParty = {
      id: String(getObjectId(client._id)),
      name: client.name ?? '',
    };

    // No deep-link → default per-client direct scope.
    if (!agreementIdParam) {
      return { kind: 'direct', counterparty: clientScopeParty };
    }

    // Deep-linked but the agreement hasn't loaded yet — render the
    // skeleton inside `PricingSheetPage` instead of seeding a stale
    // direct scope that would briefly show the wrong rows.
    if (!agreement) return undefined;

    // Mirror `AgreementScopePicker` in PricingSheetHeader: the
    // counterparty id is whichever party in `parties[]` isn't the
    // current org. Falls back to the Client's own id when the agreement
    // document is the legacy single-org shape (same rationale as above).
    const cp = getAgreementCounterparty(agreement, orgId);
    const counterparty = cp
      ? { id: String(getObjectId(cp._id)), name: cp.name ?? clientScopeParty.name }
      : clientScopeParty;

    return {
      kind: 'agreement',
      agreement: {
        id: String(agreement._id),
        label: agreement.title ?? counterparty.name,
      },
      counterparty,
    };
  }, [client._id, client.name, orgId, agreementIdParam, agreement]);

  if (!initialScope) return null;

  return (
    <PricingSheetPage
      initialScope={initialScope}
      scenarioLabels={scenarioLabels}
      lockScope
      hideScopePicker
      disableUrlSync
    />
  );
}
